// Iter89 / Phase 3I — Controlled CFM-CC-LEAPS / PMCC Auto-Scan Panel.
//
// Sibling of CFMSharesAutoScanPanel (Iter88). Combines:
//   * fresh market snapshot call chain from MarketSnapshotContext
//     (used as the short-call candidate set for the diagonal)
//   * operator-provided LEAPS / PMCC context from
//     CFMInventoryContext (existing_leaps_position, total_short_call_credits,
//     leaps_assignment_policy, allow_leaps)
// and POSTs them to the existing Iter66 CFM LEAPS preview endpoint:
//
//   POST /api/forge/toolbox/scanner/cfm-leaps-preview
//
// The panel uses the operator-provided LEAPS position only —
// it never tries to discover a LEAPS contract from the snapshot
// chain (snapshots are short-dated). When `leaps_calls` are not
// present in the operator-provided LEAPS position, the panel
// surfaces the discovery disclaimer rather than fabricating.
//
// HARD CONSTRAINTS (asserted by Iter89 tests):
//  * Manual-run only. No `useEffect`. No auto-fire on mount.
//  * No outbound HTTP except the CFM LEAPS preview endpoint.
//  * Never calls `/forge/candidates`, `/market-scanner/*`,
//    snapshot build endpoint, `/scanner/bpcs-preview`,
//    `/scanner/bccs-preview`, `/scanner/iron-condor-preview`,
//    `/scanner/cfm-shares-preview`, broker / account / position /
//    order / journal / admin endpoints.
//  * No `localStorage` / `sessionStorage` / `IndexedDB` / cookies.
//  * No ranking / winner / best / preferred / auto-select
//    language outside safety disclaimers.
//  * No LEAPS exercise automation. No assignment automation.

import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  ChevronDown, ChevronUp, AlertTriangle, Search, CheckCircle2,
  Clock,
} from "lucide-react";
import { API } from "@/App";
import { useMarketSnapshot } from "@/contexts/MarketSnapshotContext";
import {
  useCFMInventory, isLeapsReady,
} from "@/contexts/CFMInventoryContext";
import { useWESContext } from "@/contexts/WESContextProvider";
import {
  summarizeWesContext, applyWesToSubpayload,
} from "@/components/wesContextPayloadHelpers";
import WesProvenanceStrip from "@/components/WesProvenanceStrip";
import StrategyResultCard from "@/components/StrategyResultCard";
import { MetricChip } from "@/components/ScannerResultNormalization";
import SnapshotScanKit from "@/components/SnapshotScanKit";

const CFM_LEAPS_ENDPOINT = "/forge/toolbox/scanner/cfm-leaps-preview";
const STALE_SECONDS = 5 * 60;
const DEFAULT_MAX_SYMBOLS = 5;
const TID = "cfm-leaps-auto-scan";

function ageSeconds(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
}

function numOrNull(s) {
  if (s === "" || s === null || s === undefined) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

const SAFETY_LABELS = [
  { testid: `${TID}-banner-cfm-leaps-only`,      text: "CFM LEAPS / PMCC only." },
  { testid: `${TID}-banner-pmcc-synthetic`,      text: "PMCC is synthetic inventory." },
  { testid: `${TID}-banner-no-leaps-exercise`,   text: "No LEAPS exercise automation." },
  { testid: `${TID}-banner-no-assignment-auto`,  text: "No assignment automation." },
  { testid: `${TID}-banner-manual`,              text: "Manual scan only." },
  { testid: `${TID}-banner-no-ranking`,          text: "No ranking." },
  { testid: `${TID}-banner-no-winner`,           text: "No winner." },
  { testid: `${TID}-banner-no-broker-fetch`,     text: "No broker/account fetch." },
  { testid: `${TID}-banner-no-execution`,        text: "No trade execution." },
  { testid: `${TID}-banner-no-journal`,          text: "No journal write." },
  { testid: `${TID}-banner-no-persistence`,      text: "No persistence." },
];

function chainCounts(row) {
  const chain = Array.isArray(row?.chain) ? row.chain : [];
  return {
    puts:  chain.filter(r => r && r.type === "put").length,
    calls: chain.filter(r => r && r.type === "call").length,
  };
}

function buildExistingLeapsPosition(invRow) {
  const elp = invRow?.leaps?.existing_leaps_position || {};
  const out = {
    underlying_symbol: elp.underlying_symbol || invRow?.symbol || null,
    expiration:        elp.expiration || null,
    strike:            numOrNull(elp.strike),
    option_type:       elp.option_type || "call",
    quantity:          numOrNull(elp.quantity),
    basis:             numOrNull(elp.basis),
  };
  const d = numOrNull(elp.delta);
  if (d !== null) out.delta = d;
  const t = numOrNull(elp.theta);
  if (t !== null) out.theta = t;
  return out;
}

function buildAccountContext(invRow) {
  const l = invRow?.leaps || {};
  return {
    allow_leaps:              !!l.allow_leaps,
    existing_leaps_position:  buildExistingLeapsPosition(invRow),
    total_short_call_credits: numOrNull(l.total_short_call_credits),
    assignment_policy:        l.leaps_assignment_policy || null,
    campaign_notes:           l.campaign_notes || null,
  };
}

function buildPerSymbolPayload(row, invRow, wesRow, wesMeta) {
  const warnings = [];
  const chain = Array.isArray(row.chain) ? row.chain : [];
  const shortCalls = chain.filter(r => r && r.type === "call");
  if (shortCalls.length === 0) {
    warnings.push("Missing short-call chain — CFM LEAPS unavailable.");
  }
  if (!invRow || !isLeapsReady(invRow)) {
    warnings.push("LEAPS / PMCC context required — CFM LEAPS unavailable.");
  }
  // Snapshots only cover short-dated expirations; the LEAPS leg
  // always comes from operator-provided context.
  warnings.push(
    "Using operator-provided LEAPS position. No LEAPS discovery automation."
  );

  const sub = {
    spot_price: row.spot_price ?? null,
    wes: {},
    option_chains: {
      leaps_calls: [],
      short_calls: shortCalls,
    },
    account_context: buildAccountContext(invRow),
  };
  // Iter92 — helper emits the existing
  //   "WES context unavailable — CFM LEAPS scan may be structurally incomplete."
  // copy if the envelope has no row for this symbol.
  const wesWarnings = applyWesToSubpayload(
    sub, wesRow, wesMeta, "CFM LEAPS"
  );
  wesWarnings.forEach(w => warnings.push(w));
  if (row.ivr != null) sub.ivr = row.ivr;

  sub.snapshot_meta = {
    snapshot_id: row.__snapshot_id || null,
    snapshot_as_of: row.__snapshot_as_of || null,
    source: row.source || null,
    chosen_expiration: row.chosen_expiration || null,
    dte: row.dte ?? null,
    freshness_age_seconds: ageSeconds(row.__snapshot_as_of),
    warnings,
  };
  return { payload: sub, warnings };
}

export default function CFMLeapsAutoScanPanel() {
  const { snapshot } = useMarketSnapshot();
  const { rows: invRows, metadata: invMetadata } = useCFMInventory();
  const { wesContext } = useWESContext();
  const wesSummary = summarizeWesContext(wesContext);
  const wesMeta = {
    wesAsOf: wesSummary.asOf,
    fingerprint: wesSummary.fingerprint,
    version: wesSummary.version,
    stale: wesSummary.stale,
  };

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [maxSymbols, setMaxSymbols] = useState(String(DEFAULT_MAX_SYMBOLS));
  const [minIvr, setMinIvr] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [result, setResult] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [lastScannedAt, setLastScannedAt] = useState(null);
  const [perSymbolWarnings, setPerSymbolWarnings] = useState({});

  const rows = useMemo(() => {
    if (!snapshot || !Array.isArray(snapshot.rows)) return [];
    return snapshot.rows.filter(r => r && r.symbol);
  }, [snapshot]);

  const seedKey = `${snapshot?.snapshot_id || ""}|${invMetadata?.context_updated_at || ""}`;
  const [seededFor, setSeededFor] = useState("");
  if (seedKey !== "|" && seedKey !== seededFor) {
    const next = {};
    rows.forEach(r => {
      const inv = invRows[r.symbol];
      const { calls } = chainCounts(r);
      next[r.symbol] = calls > 0 && !!inv && isLeapsReady(inv);
    });
    setSelected(next);
    setSeededFor(seedKey);
  }

  function toggleSymbol(sym) { setSelected(p => ({ ...p, [sym]: !p[sym] })); }
  function selectAll() {
    const next = {}; rows.forEach(r => { next[r.symbol] = true; });
    setSelected(next);
  }
  function clearAll() { setSelected({}); }

  function chainAvailability(row) {
    const { puts, calls } = chainCounts(row);
    const inv = invRows[row.symbol];
    const invReady = !!inv && isLeapsReady(inv);
    return { ok: calls > 0 && invReady, puts, calls };
  }

  function buildEnvelope() {
    const maxN = Number(maxSymbols);
    const cap = Number.isFinite(maxN) && maxN > 0
      ? Math.floor(maxN) : DEFAULT_MAX_SYMBOLS;
    const minIvrNum = minIvr.trim() === "" ? null : Number(minIvr);
    const picked = rows
      .filter(r => selected[r.symbol])
      .filter(r => minIvrNum == null
        || !Number.isFinite(minIvrNum)
        || (r.ivr != null && r.ivr >= minIvrNum))
      .slice(0, cap);
    const watchlist = picked.map(r => r.symbol);
    const payloads_by_symbol = {};
    const warningsPerSymbol = {};
    picked.forEach(r => {
      const enriched = {
        ...r,
        __snapshot_id: snapshot.snapshot_id,
        __snapshot_as_of: snapshot.as_of,
      };
      const invRow = invRows[r.symbol];
      const { payload, warnings } = buildPerSymbolPayload(
        enriched,
        invRow,
        wesSummary.bySymbol[r.symbol],
        wesMeta,
      );
      payloads_by_symbol[r.symbol] = payload;
      warningsPerSymbol[r.symbol] = warnings;
    });
    return { envelope: { watchlist, payloads_by_symbol },
             warningsPerSymbol, picked };
  }

  async function runScan() {
    setErrorMsg(null);
    if (!snapshot) { setErrorMsg("Fresh snapshot required."); return; }
    const { envelope, warningsPerSymbol, picked } = buildEnvelope();
    if (picked.length === 0) {
      setErrorMsg("Select at least one snapshot symbol with ready LEAPS / PMCC context.");
      return;
    }
    setPerSymbolWarnings(warningsPerSymbol);
    setLastRequest(envelope);
    setLoading(true);
    try {
      const r = await axios.post(`${API}${CFM_LEAPS_ENDPOINT}`, envelope);
      setResult(r.data);
      setLastScannedAt(new Date().toISOString());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
          : (e?.message || "Network error");
      setErrorMsg(msg);
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  function clearResults() {
    setResult(null); setErrorMsg(null);
    setLastRequest(null); setLastScannedAt(null);
    setPerSymbolWarnings({});
  }

  const invEditedAgeS = ageSeconds(invMetadata?.context_updated_at);

  return (
    <div
      data-testid={`${TID}-panel`}
      className="mb-5 rounded-sm border border-[#A855F7]/30 bg-[#0A0A0E]"
    >
      <button
        type="button"
        data-testid={`${TID}-toggle`}
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <Search size={16} className="text-[#A855F7]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          CFM LEAPS / PMCC Controlled Auto-Scan
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · phase 3i · cfm-cc-leaps only · manual-run · no execution
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {!snapshot && (
            <div
              data-testid={`${TID}-fresh-snapshot-required`}
              className="px-4 py-2 text-[10px] font-mono text-[#FFB800] border-b border-[#26262B] flex items-center gap-2"
            >
              <AlertTriangle size={11} />
              <span>Fresh snapshot required.</span>
            </div>
          )}

          {Object.keys(invRows).length === 0 && (
            <div
              data-testid={`${TID}-leaps-context-missing`}
              className="px-4 py-2 text-[10px] font-mono text-[#FFB800] border-b border-[#26262B] flex items-center gap-2"
            >
              <AlertTriangle size={11} />
              <span>LEAPS / PMCC context required — CFM LEAPS unavailable.</span>
            </div>
          )}

          {invMetadata?.context_updated_at && (
            <div
              data-testid={`${TID}-inventory-edited-stamp`}
              className="px-4 py-1 text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66] border-b border-[#26262B] flex items-center gap-1"
            >
              <Clock size={9} />
              Inventory context last edited
              {" "}{invEditedAgeS != null ? `${invEditedAgeS}s ago` : "—"}
            </div>
          )}

          <div
            data-testid={`${TID}-leaps-discovery-disclaimer`}
            className="px-4 py-1 text-[9px] font-mono text-[#A855F7] border-b border-[#26262B] flex items-center gap-1"
          >
            <AlertTriangle size={10} />
            Using operator-provided LEAPS position. No LEAPS discovery automation.
          </div>

          <SnapshotScanKit
            snapshot={snapshot}
            rows={rows}
            staleSecondsCfg={STALE_SECONDS}
            ageSeconds={ageSeconds}
            selected={selected}
            onToggleSymbol={toggleSymbol}
            onSelectAll={selectAll}
            onClearAll={clearAll}
            chainAvailability={chainAvailability}
            maxSymbols={maxSymbols} setMaxSymbols={setMaxSymbols}
            minIvr={minIvr} setMinIvr={setMinIvr}
            loading={loading} onRun={runScan} onClear={clearResults}
            errorMsg={errorMsg}
            runLabel="Run CFM LEAPS Auto-Scan"
            lastScannedAt={lastScannedAt}
            testidPrefix={TID}
            safetyLabels={SAFETY_LABELS}
          />

          {snapshot && (
            <WesProvenanceStrip
              moduleId="cfm-leaps"
              wesSummary={wesSummary}
            />
          )}

          {snapshot && !loading && !result && !errorMsg && (
            <div
              data-testid={`${TID}-empty`}
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              No CFM LEAPS auto-scan run yet. Pick symbols with
              ready LEAPS / PMCC context and click Run CFM LEAPS
              Auto-Scan.
            </div>
          )}

          {snapshot && result && !loading && !errorMsg && (
            <CFMLeapsResult
              result={result}
              lastRequest={lastRequest}
              perSymbolWarnings={perSymbolWarnings}
              snapshot={snapshot}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── CFM LEAPS result block ─────────────────────────────────────

function CFMLeapsMetrics({ rec, testidPrefix }) {
  const s = rec?.selected_structure || rec?.option_plan || rec?.selected_candidate;
  if (!s) return null;
  const sym = rec?.symbol || "";
  const tid = suffix => `${testidPrefix}-${suffix}-${sym}`;
  const leapsLeg = s.leaps_leg || s.long_leg || s.leaps;
  const shortLeg = s.short_call_leg || s.short_leg || s.short_call;
  return (
    <div
      data-testid={tid("metrics")}
      className="mt-1 flex flex-wrap gap-x-3 gap-y-1"
    >
      <MetricChip label="LEAPS leg"
                   value={leapsLeg
                     ? `${leapsLeg.expiration || ""} ${leapsLeg.strike ?? ""}${leapsLeg.option_type ? ` ${leapsLeg.option_type}` : ""}`.trim()
                     : null}
                   testid={tid("leaps-leg")} />
      <MetricChip label="Short call"
                   value={shortLeg
                     ? `${shortLeg.strike ?? ""}${shortLeg.expiration ? ` @ ${shortLeg.expiration}` : ""}`.trim()
                     : null}
                   testid={tid("short-call-leg")} />
      <MetricChip label="Adj LEAPS basis"
                   value={s.adjusted_leaps_basis ?? s.leaps_adjusted_basis}
                   testid={tid("adjusted-leaps-basis")} />
      <MetricChip label="LEAPS BE"
                   value={s.leaps_standalone_breakeven ?? s.leaps_breakeven}
                   testid={tid("leaps-standalone-breakeven")} />
      <MetricChip label="Campaign value"
                   value={s.current_campaign_value ?? s.campaign_value}
                   testid={tid("current-campaign-value")} />
      <MetricChip label="Campaign P/L"
                   value={s.campaign_pl ?? s.campaign_profit}
                   testid={tid("campaign-pl")} />
      <MetricChip label="Net delta"
                   value={s.net_delta}
                   testid={tid("net-delta")} />
      <MetricChip label="Theta carry"
                   value={s.theta_carry_ratio ?? s.theta_ratio}
                   testid={tid("theta-carry-ratio")} />
      <MetricChip label="Assignment risk"
                   value={s.assignment_risk_diagnostic ?? s.assignment_risk}
                   testid={tid("assignment-risk-diagnostic")} />
    </div>
  );
}

function CFMLeapsResult({ result, lastRequest, perSymbolWarnings, snapshot }) {
  const counts = result.counts || {};
  return (
    <div data-testid={`${TID}-result`} className="px-4 py-3">
      <div className="flex items-center gap-2 flex-wrap mb-3 text-[10px] font-mono">
        <CheckCircle2 size={11} className="text-[#00FF88]" />
        <span data-testid={`${TID}-result-version`}
              className="text-[#8A8A93] uppercase tracking-[0.12em]">
          {result.scanner_version}
        </span>
        <span data-testid={`${TID}-result-module-id`}
              className="px-2 py-0.5 rounded-sm border border-[#A855F7]/40 bg-[#A855F7]/10 text-[9px] uppercase tracking-[0.14em] text-[#A855F7]">
          {result.module_id}
        </span>
        <span data-testid={`${TID}-result-total`} className="text-[#E0E0E0]">
          {result.total_symbols} symbols scanned
        </span>
        <span className="ml-auto inline-flex items-center gap-3">
          {["enter", "watch", "defend_leaps", "close_diagonal",
            "assignment_risk", "blocked", "no_trade", "error"].map(k => (
            <span key={k} data-testid={`${TID}-result-count-${k}`}
                  className="flex items-center gap-1 text-[#8A8A93]">
              <span className="uppercase tracking-[0.12em]">{k}</span>
              <span className="font-bold text-[#E0E0E0]">{counts[k] ?? 0}</span>
            </span>
          ))}
        </span>
      </div>

      {Array.isArray(result.warnings) && result.warnings.length > 0 && (
        <div data-testid={`${TID}-result-warnings`}
             className="mb-3 text-[10px] font-mono text-[#FFB800]">
          {result.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1">
              <AlertTriangle size={10} className="mt-0.5 shrink-0" />{w}
            </div>
          ))}
        </div>
      )}

      <ul className="space-y-2">
        {(result.results || []).map(rec => {
          const sym = rec.symbol;
          const localWarnings = perSymbolWarnings[sym] || [];
          const merged = {
            ...rec,
            warnings: [
              ...(Array.isArray(rec.warnings) ? rec.warnings : []),
              ...localWarnings,
            ],
          };
          const rowSnap = (snapshot?.rows || []).find(r => r && r.symbol === sym);
          const rowAge = ageSeconds(snapshot?.as_of);
          return (
            <StrategyResultCard
              key={sym} rec={merged} moduleLabel="CFM_CC_LEAPS"
              accent={{
                border: "border-[#A855F7]/30",
                glow:   "bg-[#A855F7]/5",
                text:   "text-[#A855F7]",
              }}
              testidPrefix={`${TID}-result-row`}
            >
              <div
                data-testid={`${TID}-result-row-snapshot-${sym}`}
                className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]"
              >
                snapshot {snapshot?.snapshot_id?.slice(0, 8) || "—"} ·
                {" "}{rowAge != null ? `${rowAge}s ago` : "—"} ·
                {" "}SRC {rowSnap?.source || "—"} ·
                {" "}EXP {rowSnap?.chosen_expiration || "—"}
              </div>
              <CFMLeapsMetrics
                rec={merged}
                testidPrefix={`${TID}-result-row`}
              />
            </StrategyResultCard>
          );
        })}
      </ul>

      <details
        data-testid={`${TID}-result-request-json`}
        className="mt-3 border border-[#26262B] rounded-sm"
      >
        <summary className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Request payload used (read-only)
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#08080A] p-2 max-h-64 overflow-auto">
          {JSON.stringify(lastRequest, null, 2)}
        </pre>
      </details>
    </div>
  );
}
